import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface DangerousDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Short title shown in the dialog header. */
  title: string;
  /** Plain-language description of what will be deleted. Include the entity name. */
  description: string;
  /** Word the user must type to enable the confirm button. Defaults to "DELETE". */
  confirmWord?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  loading?: boolean;
  onConfirm: () => void;
}

/**
 * Type-to-confirm dialog for destructive admin actions (delete admin, drop
 * cloud server, etc.). Designed to defeat the "one-click XSS hijack" threat —
 * a stolen admin session can't `.click()` its way through a free-text input
 * that requires the user to type a specific word.
 *
 * Don't use for non-destructive confirms — friction is intentional and should
 * stay scarce. Pair with backend re-auth (password) for truly catastrophic
 * actions like "delete all admins" or "wipe DB".
 */
export function DangerousDeleteDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmWord = "DELETE",
  confirmLabel = "Eliminar",
  cancelLabel = "Cancelar",
  loading = false,
  onConfirm,
}: DangerousDeleteDialogProps) {
  const [typed, setTyped] = useState("");

  // Reset the typed value whenever the dialog re-opens, so a stale "DELETE"
  // string can't carry over from a previous failed attempt.
  useEffect(() => {
    if (open) setTyped("");
  }, [open]);

  const canConfirm = typed.trim() === confirmWord && !loading;

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          <AlertDialogDescription>{description}</AlertDialogDescription>
        </AlertDialogHeader>

        <div className="space-y-2 py-2">
          <Label htmlFor="delete-confirm">
            Escribí{" "}
            <span className="font-mono font-semibold">{confirmWord}</span> para
            confirmar
          </Label>
          <Input
            id="delete-confirm"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            placeholder={confirmWord}
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={!canConfirm}
            onClick={(e) => {
              if (!canConfirm) {
                e.preventDefault();
                return;
              }
              onConfirm();
            }}
            className="bg-red-500 hover:bg-red-600 focus-visible:ring-red-500"
          >
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
